import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { requestWithFallback } from '../api/httpClient';
import { handlePushNotificationNavigation } from '../notifications/notificationRouter';

import { readStoredAuth } from '../api/authApi';

let isPushInitialized = false;

/**
 * Registra el token FCM en el backend Laravel
 */
async function registerDeviceToken(fcmToken) {
    try {
        const storedAuth = await readStoredAuth();
        const jwtToken = storedAuth?.token || null;

        if (!jwtToken) {
            console.warn('[PushService] No se pudo registrar token FCM: no hay sesión JWT activa');
            return;
        }

        console.log('[PushService] Registrando token FCM en backend...');

        const [deviceInfo, deviceId] = await Promise.all([
            Device.getInfo(),
            Device.getId(),
        ]);
        const model = [deviceInfo.manufacturer, deviceInfo.model]
            .filter(Boolean)
            .filter((value, index, values) => index === 0 || value !== values[index - 1])
            .join(' ');

        const payload = {
            token: fcmToken,
            platform: Capacitor.getPlatform(),
            deviceId: deviceId.identifier,
            appVersion: String(import.meta.env.VITE_APP_VERSION || 'desconocida'),
            osVersion: deviceInfo.osVersion || null,
            model: model || deviceInfo.model || null,
        };

        await requestWithFallback({
            moduleName: 'push',
            endpoint: 'register',
            endpointIsKey: true,
            method: 'post',
            token: jwtToken,
            data: payload,
        });
        console.log('[PushService] Token FCM registrado exitosamente en el backend');
    } catch (error) {
        console.error('[PushService] Error registrando token FCM en backend:', error);
    }
}

/**
 * Inicializa el sistema de notificaciones push en la app
 */
export async function initializePushNotifications(navigate) {
    if (import.meta.env.VITE_DISABLE_PUSH_NOTIFICATIONS === 'true') {
        console.log('[PushService] Notificaciones push desactivadas para esta variante de pruebas.');
        return;
    }

    if (!Capacitor.isNativePlatform()) {
        console.log('[PushService] Notificaciones push omitidas: no es plataforma nativa (Web)');
        return;
    }

    if (isPushInitialized) {
        return;
    }

    try {
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
            console.warn('[PushService] Permiso de notificaciones push no concedido:', permStatus.receive);
            return;
        }

        // 1. Limpiar listeners previos y registrar los nuevos ANTES de register()
        await PushNotifications.removeAllListeners();

        // Éxito al registrar FCM token
        PushNotifications.addListener('registration', async (token) => {
            console.log('[PushService] FCM Token obtenido:', token?.value);
            if (token?.value) {
                await registerDeviceToken(token.value);
            }
        });

        // Error al registrar
        PushNotifications.addListener('registrationError', (error) => {
            console.error('[PushService] Error al registrar FCM:', error);
        });

        // Notificación recibida en primer plano (Foreground)
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('[PushService] Push recibida en primer plano:', notification);
        });

        // Acción al tocar la notificación
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            console.log('[PushService] Push tocada por el usuario:', action);
            if (navigate) {
                handlePushNotificationNavigation(action, navigate);
            }
        });

        // Crear canales de notificación corporativos con sonidos de identidad Maxiseguridad en Android
        if (Capacitor.getPlatform() === 'android') {
            try {
                // 1. Canal estándar (Avisos y Coordinación)
                await PushNotifications.createChannel({
                    id: 'visitas_channel',
                    name: 'Avisos y Coordinación de Visitas',
                    description: 'Notificaciones prioritarias y operativas de servicios',
                    sound: 'notification.wav',
                    importance: 5,
                    visibility: 1,
                    vibration: true,
                });

                // 2. Canal de éxito / confirmación (Sync y Formularios guardados)
                await PushNotifications.createChannel({
                    id: 'success_channel',
                    name: 'Confirmaciones y Éxitos',
                    description: 'Confirmaciones de visitas finalizadas y formularios',
                    sound: 'success.wav',
                    importance: 4,
                    visibility: 1,
                    vibration: true,
                });

                // 3. Canal de alertas críticas
                await PushNotifications.createChannel({
                    id: 'alert_channel',
                    name: 'Alertas Críticas y Cancelaciones',
                    description: 'Avisos urgentes de cancelación o seguridad',
                    sound: 'alert.wav',
                    importance: 5,
                    visibility: 1,
                    vibration: true,
                });

                console.log('[PushService] Canales de notificación Maxiseguridad creados exitosamente');
            } catch (chanError) {
                console.warn('[PushService] No se pudieron crear los canales de notificación:', chanError);
            }
        }

        // 2. Registrar con FCM / APNS ahora que los listeners y canal ya están listos
        await PushNotifications.register();
        isPushInitialized = true;
        console.log('[PushService] PushNotifications.register() ejecutado');

    } catch (error) {
        console.error('[PushService] Error inicializando notificaciones push:', error);
    }
}

/**
 * Desregistra las notificaciones al cerrar sesión
 */
export async function unregisterPushNotifications() {
    if (!Capacitor.isNativePlatform() || !isPushInitialized) {
        return;
    }

    try {
        const storedAuth = await readStoredAuth();
        const jwtToken = storedAuth?.token || null;

        await requestWithFallback({
            moduleName: 'push',
            endpoint: 'unregister',
            endpointIsKey: true,
            method: 'post',
            token: jwtToken,
        }).catch(() => {});

        await PushNotifications.removeAllListeners();
        isPushInitialized = false;
        console.log('[PushService] Push notifications desregistradas');
    } catch (error) {
        console.error('[PushService] Error al desregistrar push:', error);
    }
}
